import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Divider,
  Collapse,
  Spin,
  message,
  Input,
  Select,
  Form,
  Alert,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  SaveOutlined,
  UndoOutlined,
  RedoOutlined,
  HistoryOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { 
  getActionPlanById, 
  updateActionPlanSection, 
  updateActionPlanStatus,
  createActionPlan
} from '../services/actionPlanService';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Action Plan Editor Component
 * Provides inline editing capabilities for action plan sections
 */
const ActionPlanEditor = ({ visible, actionPlan, patientId, onClose, onSave }) => {
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSections, setEditingSections] = useState({});
  const [sectionChanges, setSectionChanges] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [form] = Form.useForm();

  // Load full plan details when modal opens, or initialize for new plan
  useEffect(() => {
    if (visible) {
      if (actionPlan?._id) {
        loadPlanDetails();
      } else {
        // Initialize for new action plan
        initializeNewPlan();
      }
    }
  }, [visible, actionPlan?._id]);

  const initializeNewPlan = () => {
    const newPlan = {
      title: '',
      description: '',
      status: 'draft',
      sections: {
        assessment: { content: '', status: 'pending' },
        goals: { content: '', status: 'pending' },
        interventions: { content: '', status: 'pending' },
        monitoring: { content: '', status: 'pending' }
      },
      createdAt: new Date().toISOString(),
      isNew: true
    };
    setPlanDetails(newPlan);
    form.setFieldsValue({
      status: 'draft',
      title: '',
      description: ''
    });
  };

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = Object.keys(sectionChanges).length > 0;
    setUnsavedChanges(hasChanges);
  }, [sectionChanges]);

  const loadPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await getActionPlanById(actionPlan._id, true);
      if (response.success) {
        setPlanDetails(response.data.actionPlan);
        // Initialize form with current status
        form.setFieldsValue({
          status: response.data.actionPlan.status
        });
      }
    } catch (error) {
      console.error('Error loading plan details:', error);
      message.error('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  // Identify a section by its unique subdocument _id when available, falling
  // back to sectionId for not-yet-persisted (new plan) sections. A plan can
  // contain several sections sharing the same sectionId enum value, so keying
  // edit state / save targets by sectionId collided across duplicates and
  // edits hit the wrong section. _id is unique per section.
  const getSectionKey = (section) => section?._id || section?.sectionId;

  const handleSectionEdit = (sectionId, isEditing) => {
    setEditingSections(prev => ({
      ...prev,
      [sectionId]: isEditing
    }));

    // If canceling edit, remove any unsaved changes for this section
    if (!isEditing && sectionChanges[sectionId]) {
      const newChanges = { ...sectionChanges };
      delete newChanges[sectionId];
      setSectionChanges(newChanges);
    }
  };

  const handleSectionChange = (sectionId, newContent) => {
    setSectionChanges(prev => ({
      ...prev,
      [sectionId]: newContent
    }));
  };

  const handleSectionSave = async (sectionId, changeDescription = '') => {
    try {
      setSaving(true);
      const newContent = sectionChanges[sectionId];

      // Get current section content if no changes were made. `sectionId` here
      // is the section key (its unique _id for persisted plans), so match by
      // the same key — not by the (possibly duplicated) sectionId enum value.
      const currentSection = planDetails?.sections?.find(s => getSectionKey(s) === sectionId);
      const contentToSave = newContent || currentSection?.currentContent || '';

      const response = await updateActionPlanSection(
        actionPlan._id,
        sectionId,
        contentToSave,
        changeDescription || 'Section saved'
      );

      if (response.success) {
        message.success('Section updated successfully');

        // Update local state
        setPlanDetails(prev => ({
          ...prev,
          sections: prev.sections.map(section =>
            getSectionKey(section) === sectionId
              ? { ...section, currentContent: newContent, isModified: true }
              : section
          )
        }));

        // Clear changes and exit edit mode
        const newChanges = { ...sectionChanges };
        delete newChanges[sectionId];
        setSectionChanges(newChanges);

        setEditingSections(prev => ({
          ...prev,
          [sectionId]: false
        }));
      }
    } catch (error) {
      console.error('Error saving section:', error);
      message.error(`Failed to save section: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      const response = await updateActionPlanStatus(
        actionPlan._id,
        newStatus,
        `Status changed to ${newStatus}`
      );

      if (response.success) {
        message.success('Status updated successfully');
        setPlanDetails(prev => ({
          ...prev,
          status: newStatus
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      if (planDetails?.isNew) {
        // Create new action plan
        const formValues = form.getFieldsValue();
        const actionPlanData = {
          title: formValues.title || 'New Action Plan',
          description: formValues.description || '',
          status: formValues.status || 'draft',
          sections: {}
        };

        // Add section changes to the new plan
        Object.keys(sectionChanges).forEach(sectionId => {
          actionPlanData.sections[sectionId] = {
            content: sectionChanges[sectionId],
            status: 'completed'
          };
        });

        const response = await createActionPlan(patientId, actionPlanData);
        if (response.success) {
          message.success('Action plan created successfully');
          // Notify parent component to refresh list
          if (onSave) onSave();
          onClose();
        }
      } else {
        // Update existing action plan
        const sectionIds = Object.keys(sectionChanges);
        
        if (sectionIds.length === 0) {
          message.warning('No changes to save');
          return;
        }

        // Save all changed sections
        for (const sectionId of sectionIds) {
          await updateActionPlanSection(
            actionPlan._id,
            sectionId,
            sectionChanges[sectionId],
            'Bulk save operation'
          );
        }

        message.success(`${sectionIds.length} sections updated successfully`);
        
        // Clear all changes and exit edit modes
        setSectionChanges({});
        setEditingSections({});
        
        // Reload plan details
        await loadPlanDetails();
        
        // Notify parent component
        if (onSave) onSave();
      }
      
    } catch (error) {
      console.error('Error saving action plan:', error);
      message.error('Failed to save action plan');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (unsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. What would you like to do?',
        okText: 'Discard Changes & Close',
        okType: 'danger',
        cancelText: 'Keep Editing',
        onOk: () => {
          setSectionChanges({});
          setEditingSections({});
          setUnsavedChanges(false);
          onClose();
        },
        onCancel: () => {
          // Just stay in the modal - no action needed
        }
      });
    } else {
      onClose();
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'orange',
      active: 'green',
      archived: 'blue',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const renderSectionEditor = (section) => {
    const sectionKey = getSectionKey(section);
    const isEditing = editingSections[sectionKey];
    const currentContent = sectionChanges[sectionKey] || section.currentContent;
    const hasChanges = sectionChanges[sectionKey] !== undefined;

    return (
      <div>
        {!isEditing ? (
          <div>
            <div 
              style={{ 
                minHeight: '100px', 
                padding: '12px', 
                border: '1px dashed #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#fafafa',
                whiteSpace: 'pre-wrap'
              }}
            >
              {currentContent || 'No content'}
            </div>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleSectionEdit(sectionKey, true)}
              >
                Edit Section
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <TextArea
              value={currentContent}
              onChange={(e) => handleSectionChange(sectionKey, e.target.value)}
              rows={10}
              placeholder="Enter section content..."
              style={{ marginBottom: 12 }}
            />
            <Row justify="space-between" align="middle">
              <Col>
                {hasChanges && (
                  <Tag color="orange" icon={<WarningOutlined />}>
                    Unsaved Changes
                  </Tag>
                )}
              </Col>
              <Col>
                <Space>
                  <Button
                    onClick={() => handleSectionEdit(sectionKey, false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={() => handleSectionSave(sectionKey)}
                  >
                    Save Section
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  };

  const renderActionPlanSections = () => {
    if (!planDetails?.sections || planDetails.sections.length === 0) {
      return <Text type="secondary">No sections available</Text>;
    }

    const sectionOrder = [
      'tier_recommendations',
      'active_problems',
      'medications',
      'labs_needed',
      'imaging_procedures',
      'referrals',
      'smart_goals',
      'vaccinations',
      'nursing_orders',
      'appointments_followup'
    ];

    // Ensure sections is an array before sorting
    const sections = planDetails.sections || [];
    if (!Array.isArray(sections)) {
      console.warn('ActionPlanEditor: planDetails.sections is not an array:', sections);
      return <Text type="secondary">No sections available</Text>;
    }

    const sortedSections = sections.sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.sectionId);
      const bIndex = sectionOrder.indexOf(b.sectionId);
      return aIndex - bIndex;
    });

    return (
      <Collapse
        defaultActiveKey={sortedSections.map(s => getSectionKey(s))}
        ghost
      >
        {sortedSections.map((section) => (
          <Panel
            header={
              <Space>
                <Text strong>{section.title}</Text>
                {section.isModified && (
                  <Tag color="blue" size="small">Modified</Tag>
                )}
                {editingSections[getSectionKey(section)] && (
                  <Tag color="green" size="small">Editing</Tag>
                )}
                {sectionChanges[getSectionKey(section)] !== undefined && (
                  <Tag color="orange" size="small">Unsaved</Tag>
                )}
              </Space>
            }
            key={getSectionKey(section)}
          >
            {renderSectionEditor(section)}
          </Panel>
        ))}
      </Collapse>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          <span>{planDetails?.title || 'Edit Action Plan'}</span>
          {unsavedChanges && (
            <Tag color="orange" icon={<WarningOutlined />}>
              Unsaved Changes
            </Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={1400}
      footer={
        <Space>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSaveAll}
            disabled={!unsavedChanges}
          >
            Save All Changes{unsavedChanges ? ` (${Object.keys(sectionChanges).length})` : ''}
          </Button>
        </Space>
      }
      className="action-plan-editor-modal"
    >
      <Spin spinning={loading}>
        {planDetails && (
          <div>
            {/* Plan Status Management */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Form form={form} layout="vertical">
                {planDetails?.isNew && (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="title" label="Action Plan Title" rules={[{ required: true, message: 'Title is required' }]}>
                          <Input placeholder="Enter action plan title" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="status" label="Status">
                          <Select style={{ width: '100%' }}>
                            <Option value="draft">Draft</Option>
                            <Option value="active">Active</Option>
                            <Option value="archived">Archived</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="description" label="Description">
                      <TextArea rows={3} placeholder="Enter action plan description (optional)" />
                    </Form.Item>
                  </>
                )}
                
                {!planDetails?.isNew && (
                  <Row gutter={16} align="middle">
                    <Col span={12}>
                      <Space>
                        <Text strong>Plan Status:</Text>
                        <Tag color={getStatusColor(planDetails.status)}>
                          {planDetails.status?.toUpperCase()}
                        </Tag>
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="status" label="Change Status">
                        <Select
                          style={{ width: 120 }}
                          onChange={handleStatusChange}
                          loading={saving}
                        >
                          <Option value="draft">Draft</Option>
                          <Option value="active">Active</Option>
                          <Option value="archived">Archived</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </Form>
            </Card>

            {/* Editing Instructions */}
            <Alert
              message="Editing Instructions"
              description="Click 'Edit Section' to modify individual sections. Changes are saved per section. Use 'Save All Changes' to save multiple sections at once."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* Action Plan Content Editor */}
            <Card title="Edit Action Plan Content">
              {renderActionPlanSections()}
            </Card>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ActionPlanEditor;
